import React, { useState, useRef, useEffect } from 'react'
import { File, Edit, Download, Share2, Trash2, MoreVertical, Loader, ChevronUp, ChevronDown } from 'lucide-react'
import { formatFileSize, getFriendlyFileType } from '../utils/fileHelpers'
import { useLocalStorage } from '../hooks/useLocalStorage'

interface FileItem {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  mimeType?: string
  modifiedAt: string
  path: string
}

interface FileListProps {
  files: FileItem[]
  selectedItems: string[]
  onSelectAll: () => void
  onItemClick: (item: FileItem) => void
  onItemSelect: (itemId: string) => void
  onRenameItem: (item: FileItem) => void
  onCreateShareLink: (item: FileItem) => void
  onDeleteItem: (item: FileItem) => void
  sharingItemId?: string
  loading: boolean
}

type SortField = 'name' | 'type' | 'size' | 'modifiedAt'
type SortDirection = 'asc' | 'desc'

const FileList: React.FC<FileListProps> = ({
  files,
  selectedItems,
  onSelectAll,
  onItemClick,
  onItemSelect,
  onRenameItem,
  onCreateShareLink,
  onDeleteItem,
  sharingItemId,
  loading
}) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({})
  
  // Sorting state with localStorage persistence
  const [sortField, setSortField] = useLocalStorage<SortField>('fileListSortField', 'name')
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('fileListSortDirection', 'asc')
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown]?.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdown])
  
  const toggleDropdown = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation()
    setActiveDropdown(activeDropdown === itemId ? null : itemId)
  }
  
  // Handle sorting
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field and default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
  }
  
  // Sort the files
  const sortedFiles = [...files].sort((a, b) => {
    // Always put folders first, then sort by the selected field
    if (a.type === 'folder' && b.type !== 'folder') return -1
    if (a.type !== 'folder' && b.type === 'folder') return 1
    
    // Sort by the selected field
    let comparison = 0
    
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name)
        break
      case 'type':
        if (a.type === b.type) {
          // If both are files, compare by mime type
          if (a.type === 'file' && b.type === 'file') {
            const aType = a.mimeType || ''
            const bType = b.mimeType || ''
            comparison = aType.localeCompare(bType)
          } else {
            // If both are folders, sort by name
            comparison = a.name.localeCompare(b.name)
          }
        }
        break
      case 'size':
        // For folders or undefined sizes, treat as 0
        const aSize = a.size || 0
        const bSize = b.size || 0
        comparison = aSize - bSize
        break
      case 'modifiedAt':
        comparison = new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime()
        break
    }
    
    // Apply sort direction
    return sortDirection === 'asc' ? comparison : -comparison
  })
  
  // Custom folder icon with solid fill
  const FolderIcon = ({ size = 20 }: { size?: number }) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="var(--color-secondary)" 
      stroke="var(--color-secondary)" 
      strokeWidth="0.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className="mr-2"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  )
  
  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null
    
    return sortDirection === 'asc' 
      ? <ChevronUp size={14} className="ml-1" /> 
      : <ChevronDown size={14} className="ml-1" />
  }
  
  if (loading) {
    return (
      <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--color-primary)] mx-auto mb-4"></div>
        <p>Loading files...</p>
      </div>
    )
  }
  
  if (files.length === 0) {
    return (
      <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-8 text-center">
        <p className="text-[var(--color-accent)] mb-2">No files found</p>
        <p>This folder is empty. Upload files or create a new folder.</p>
      </div>
    )
  }
  
  return (
    <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light overflow-hidden">
      <div className="overflow-auto max-h-[calc(100vh-280px)]">
        <table className="min-w-full divide-y divide-[var(--color-accent)] dark:divide-[var(--color-secondary)] dark:divide-opacity-30">
          <thead className="bg-[var(--color-background)] dark:bg-[#1f1f1f] sticky top-0 z-10">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-[var(--color-accent)] dark:border-[var(--color-secondary)]"
                  checked={selectedItems.length === files.length && files.length > 0}
                  onChange={onSelectAll}
                  readOnly={files.length === 0}
                />
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Name
                  {renderSortIndicator('name')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('type')}
              >
                <div className="flex items-center">
                  Type
                  {renderSortIndicator('type')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('size')}
              >
                <div className="flex items-center">
                  Size
                  {renderSortIndicator('size')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('modifiedAt')}
              >
                <div className="flex items-center">
                  Modified
                  {renderSortIndicator('modifiedAt')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-[#2a2a2a] divide-y divide-[var(--color-accent)] dark:divide-[var(--color-secondary)] dark:divide-opacity-30">
            {sortedFiles.map((file) => (
              <tr 
                key={file.id}
                className={`hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] ${
                  selectedItems.includes(file.id) ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f]' : ''
                }`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-[var(--color-accent)] dark:border-[var(--color-secondary)]"
                    checked={selectedItems.includes(file.id)}
                    onChange={() => onItemSelect(file.id)}
                  />
                </td>
                <td 
                  className="px-6 py-4 whitespace-nowrap cursor-pointer"
                  onClick={() => onItemClick(file)}
                >
                  <div className="flex items-center">
                    {file.type === 'folder' ? (
                      <FolderIcon />
                    ) : (
                      <File size={20} className="text-[var(--color-primary)] mr-2" />
                    )}
                    <span className="font-medium truncate max-w-xs" title={file.name}>{file.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {file.type === 'folder' ? 'Folder' : getFriendlyFileType(file.mimeType || '')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {file.type === 'file' && file.size !== undefined
                    ? formatFileSize(file.size)
                    : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {new Date(file.modifiedAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                      onClick={() => onRenameItem(file)}
                      title="Rename"
                    >
                      <Edit size={16} />
                    </button>
                    {file.type === 'file' && (
                      <button
                        className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                        onClick={() => console.log('Download file:', file.name)}
                        title="Download"
                      >
                        <Download size={16} />
                      </button>
                    )}
                    <button
                      className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                      onClick={() => onCreateShareLink(file)}
                      title="Share"
                      disabled={sharingItemId === file.id}
                    >
                      {sharingItemId === file.id ? (
                        <Loader size={16} className="animate-spin" />
                      ) : (
                        <Share2 size={16} />
                      )}
                    </button>
                    
                    <div className="relative" style={{ overflow: 'visible' }}>
                      <button
                        className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                        onClick={(e) => toggleDropdown(e, file.id)}
                        title="More options"
                      >
                        <MoreVertical size={16} />
                      </button>
                      
                      {activeDropdown === file.id && (
                        <div 
                          ref={(el) => dropdownRefs.current[file.id] = el}
                          className="absolute right-0 top-0 mt-8 w-40 bg-white dark:bg-[#2a2a2a] shadow-custom-medium dark:shadow-lg dark:shadow-black/30 rounded-md z-50 border border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30"
                          onClick={(e) => e.stopPropagation()}
                          style={{ overflow: 'visible' }}
                        >
                          <button
                            className="w-full text-left px-4 py-2 hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)] flex items-center"
                            onClick={(e) => {
                              e.stopPropagation()
                              onRenameItem(file)
                              setActiveDropdown(null)
                            }}
                          >
                            <Edit size={16} className="mr-2" />
                            Rename
                          </button>
                          {file.type === 'file' && (
                            <button
                              className="w-full text-left px-4 py-2 hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)] flex items-center"
                              onClick={(e) => {
                                e.stopPropagation()
                                console.log('Download file:', file.name)
                                setActiveDropdown(null)
                              }}
                            >
                              <Download size={16} className="mr-2" />
                              Download
                            </button>
                          )}
                          <button
                            className="w-full text-left px-4 py-2 hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)] flex items-center"
                            onClick={(e) => {
                              e.stopPropagation()
                              onCreateShareLink(file)
                              setActiveDropdown(null)
                            }}
                            disabled={sharingItemId === file.id}
                          >
                            {sharingItemId === file.id ? (
                              <>
                                <Loader size={16} className="mr-2 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              <>
                                <Share2 size={16} className="mr-2" />
                                Share
                              </>
                            )}
                          </button>
                          <button
                            className="w-full text-left px-4 py-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center"
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteItem(file)
                              setActiveDropdown(null)
                            }}
                          >
                            <Trash2 size={16} className="mr-2" />
                            Move to Trash
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="h-12"></div> {/* Extra space at the bottom for scrolling */}
    </div>
  )
}

export default FileList